import { useEffect, useRef } from 'react';

// Keyboard behaviour for a hand-rolled dialog or menu, as ConfirmModal does
// it: Escape closes it, focus moves into it when it opens (initialFocusRef),
// and goes back to whatever had focus before (the button that opened it)
// when it closes. The support modal, the account menu, the team switcher and
// the SMS modals ignored Escape and left focus on the page behind them.
//
// onClose may be null to ignore Escape for a while (a save in flight), or
// for a dialog that has no way to be dismissed.
export default function useDialogDismiss(open, onClose, { initialFocusRef = null, restoreFocus = true } = {}) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    const opener = document.activeElement;
    initialFocusRef?.current?.focus?.();

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || !onCloseRef.current) return;
      // A list or menu inside the dialog that already used this Escape to
      // close itself marks it handled; the dialog stays open.
      if (event.defaultPrevented) return;
      onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      if (restoreFocus && opener && opener !== document.body && opener.isConnected) opener.focus?.();
    };
    // initialFocusRef and restoreFocus are fixed for a dialog's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
